import React, { useState, KeyboardEvent } from "react";
import {
  Box,
  Button,
  Chip,
  OutlinedInput,
  InputAdornment,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GradientButton } from "./RegisterTalentForm";

interface StepTwoProps {
  handleBack: () => void;
  handleNext: () => void;
  values: any;
  setFieldValue: (field: string, value: any) => void;
}

// ✅ Predefined skill options
const predefinedSkills = [
  "Songwriting Skills",
  "Stage Presence",
  "Lyric Writing",
  "Choreography Creation",
  "Music Fundamentals",
  "Mixing",
  "Instrument Proficiency",
  "Creative Improvisation",
  "Bharatnatyam",
  "Playing Guitar",
  "Vocal Coaching",
  "Instrumental Lessons",
  "Dance Fitness",
  "Mashups",
  "Audio Editing & Mastering",
  "Event Choreography",
  "Sound Effects & Drops",
  "DJ Branding",
];

// ✅ Styled chip container
const ChipInputBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  border: "1px solid #ccc",
  borderRadius: 8,
  minHeight: "56px",
  "&:focus-within": {
    borderColor: "#8E2DE2",
  },
}));

export const StepTwo: React.FC<StepTwoProps> = ({
  handleBack,
  handleNext,
  values,
  setFieldValue,
}) => {
  const [input, setInput] = useState("");

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = input.trim();
      if (trimmed && !values.skills.includes(trimmed)) {
        setFieldValue("skills", [...values.skills, trimmed]);
      }
      setInput("");
    }
  };

  const handleDelete = (skillToDelete: string) => {
    setFieldValue(
      "skills",
      values.skills.filter((s: string) => s !== skillToDelete)
    );
  };

  const handleChipClick = (skill: string) => {
    if (!values.skills.includes(skill)) {
      setFieldValue("skills", [...values.skills, skill]);
    } else {
      handleDelete(skill);
    }
  };

  const filteredSuggestions = predefinedSkills.filter(
    (skill) =>
      skill.toLowerCase().includes(input.toLowerCase()) &&
      !values.skills.includes(skill)
  );

  return (
    <>
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 700, 
          mb: 3,
          fontSize: { xs: '18px', sm: '24px' }
        }}
      >
        What skills do you have?
      </Typography>

      {/* Chip-style input */}
      <ChipInputBox>
        {values.skills.map((skill: string) => (
          <Chip
            key={skill}
            label={skill}
            onDelete={() => handleDelete(skill)}
            color="primary"
            sx={{ 
              fontSize: { xs: "12px", sm: "14px" },
              height: { xs: '24px', sm: '32px' },
              '& .MuiChip-label': {
                px: { xs: 1, sm: 1.5 }
              }
            }}
          />
        ))}
        <OutlinedInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type or select skills..."
          sx={{
            border: "none",
            backgroundColor: "var(--neutral-50)",
            flex: 1,
            minWidth: { xs: 120, sm: 150 },
            fontSize: { xs: "12px", sm: "14px" },
            "& fieldset": { border: "none" },
            height: { xs: '32px', sm: '40px' },
          }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon sx={{ 
                color: "#999",
                fontSize: { xs: '18px', sm: '20px' }
              }} />
            </InputAdornment>
          }
        />
      </ChipInputBox>

      {/* Suggestions */}
      <Stack 
        direction="row" 
        flexWrap="wrap" 
        gap={{ xs: 0.5, sm: 1 }} 
        mt={{ xs: 1.5, sm: 2 }} 
        mb={{ xs: 3, sm: 4 }}
      >
        {filteredSuggestions.map((skill) => (
          <Chip
            key={skill}
            label={skill}
            onClick={() => handleChipClick(skill)}
            sx={{
              borderRadius: "30px",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              padding: { xs: "6px 10px", sm: "8px 14px" },
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              height: { xs: '24px', sm: '32px' },
              "&:hover": {
                backgroundColor: "#f3f3f3",
                borderColor: "#8E2DE2",
              },
            }}
          />
        ))}
      </Stack>

      {/* Navigation Buttons */}
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between",
          gap: { xs: 2, sm: 0 },
          mt: { xs: 2, sm: 2 }
        }}
      >
        <Button 
          variant="outlined" 
          onClick={handleBack} 
          sx={{ 
            width: { xs: '100%', sm: 180 },
            height: "44px",
            font: { xs: "var(--semibold-sm)", sm: "var(--semibold-md)" },
            color: "var(--neutral-900)",
            borderRadius: "var(--spacing-3x)",
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          Previous
        </Button>
        <GradientButton 
          onClick={handleNext} 
          sx={{ 
            width: { xs: '100%', sm: 180 },
            height: "44px",
            font: { xs: "var(--semibold-sm)", sm: "var(--semibold-md)" },
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          Next
        </GradientButton>
      </Box>
    </>
  );
};
